import { IEFinderObject } from '@model';
import { toQueryString } from '@utils/misc';

import { api } from '.';

export type LiveStatus = 'live' | 'ended';
export type LiveEndReason = '' | 'author' | 'heartbeat_timeout' | 'restriction';

export interface LiveUser {
  id: number;
  nickname: string;
  picture?: IEFinderObject<'image'> | null;
}

export interface LiveSession {
  id: number;
  author: LiveUser | null;
  title: string;
  status: LiveStatus;
  startedAt: string;
  endedAt: string | null;
  endReason: LiveEndReason;
  latestSequence: number;
  heartbeatIntervalSeconds: number;
}

interface LiveEventBase {
  id: number;
  liveId: number;
  sequence: number;
  createdAt: string;
}

export interface LiveMessageEvent extends LiveEventBase {
  kind: 'message';
  user?: LiveUser;
  body: string;
}

export interface LiveDonationEvent extends LiveEventBase {
  kind: 'donation';
  user?: LiveUser;
  body: string;
  donation: {
    id: number;
    balloon: number;
  };
}

export interface LiveEndedEvent extends LiveEventBase {
  kind: 'ended';
  user?: LiveUser;
}

export interface LiveMessageHiddenEvent extends LiveEventBase {
  kind: 'message_hidden';
  body: null;
  targetEventId: number;
  targetSequence: number;
}

export type LiveEvent =
  | LiveMessageEvent
  | LiveDonationEvent
  | LiveEndedEvent
  | LiveMessageHiddenEvent;

export interface LiveEventsResponse {
  latestSequence: number;
  hasMore: boolean;
  events: LiveEvent[];
}

export interface FollowingLiveSessionsResponse {
  next: string | null;
  previous: string | null;
  results: LiveSession[];
}

export interface LiveWebSocketTicket {
  ticket: string;
  expiresInSeconds: number;
}

export interface OpenLiveSessionPayload {
  openingKey: string;
  title?: string;
}

export interface SendLiveMessagePayload {
  clientEventKey: string;
  body: string;
}

export interface SendLiveDonationPayload {
  idempotencyKey: string;
  balloon: number;
  message?: string;
}

export const fetchUserLiveSessions = async (userId: number) => {
  const { data } = await api.get<LiveSession[]>(`/users/${userId}/lives/`);
  return data;
};

export const fetchFollowingLiveSessions = async (next?: string) => {
  const url = next ?? '/users/me/following-lives/';

  if (next && process.env.NEXT_PUBLIC_API_URL) {
    const localOrigin =
      typeof window === 'undefined'
        ? 'http://localhost'
        : window.location.origin;
    const apiBase = new URL(process.env.NEXT_PUBLIC_API_URL, localOrigin);
    const apiOrigin = apiBase.origin;
    const nextUrl = new URL(next, apiBase);
    if (nextUrl.origin !== apiOrigin) {
      throw new Error('허용되지 않은 라이브 목록 페이지 URL입니다.');
    }
  }

  const { data } = await api.get<FollowingLiveSessionsResponse>(url);
  return data;
};

export const openLiveSession = async (payload: OpenLiveSessionPayload) => {
  const { data } = await api.post<LiveSession>('/lives/', payload);
  return data;
};

export const fetchLiveSession = async (liveId: number) => {
  const { data } = await api.get<LiveSession>(`/lives/${liveId}/`);
  return data;
};

export const closeLiveSession = async (liveId: number) => {
  const { data } = await api.post<LiveSession>(`/lives/${liveId}/closure/`);
  return data;
};

export const heartbeatLiveSession = async (liveId: number) => {
  const { data } = await api.post<LiveSession>(`/lives/${liveId}/heartbeats/`);
  return data;
};

export const createLiveWebSocketTicket = async (liveId: number) => {
  const { data } = await api.post<LiveWebSocketTicket>(
    `/lives/${liveId}/websocket-tickets/`
  );
  return data;
};

export const fetchLiveEvents = async (
  liveId: number,
  afterSequence?: number,
  limit = 100
) => {
  const query = toQueryString({ afterSequence, limit });
  const { data } = await api.get<LiveEventsResponse>(
    `/lives/${liveId}/events/${query}`
  );
  return data;
};

export const sendLiveMessage = async (
  liveId: number,
  payload: SendLiveMessagePayload
) => {
  const { data } = await api.post<LiveMessageEvent>(
    `/lives/${liveId}/messages/`,
    payload
  );
  return data;
};

export const sendLiveDonation = async (
  liveId: number,
  payload: SendLiveDonationPayload
) => {
  const { data } = await api.post<LiveDonationEvent>(
    `/lives/${liveId}/donations/`,
    payload
  );
  return data;
};

export const getLiveWebSocketUrl = (
  liveId: number,
  ticket: string,
  apiUrl = process.env.NEXT_PUBLIC_API_URL
) => {
  if (!apiUrl) {
    throw new Error('라이브 WebSocket URL을 만들 수 없습니다.');
  }

  const localOrigin =
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  const url = new URL(apiUrl, localOrigin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = `/ws/lives/${liveId}/`;
  url.search = '';
  url.hash = '';
  url.searchParams.set('ticket', ticket);
  return url.toString();
};
